import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { FACTION_CSS_COLORS, FACTION_NAMES, GamePlayerStatus }
    from '../../../Constants';
import { FACTION_BACKS_URL } from '../../../Settings';

import './choose-faction.css';


// Manual §3 in-game faction-pick ceremony. Each seat (clockwise from
// Player One) commits a Faction at choice pk=35. Both the active
// picker and other (observer) seats see a full-screen paged Swiper of
// the six triangle-back hero images — one slide per faction in
// canonical id order — with arrow + dot navigation. Observers can
// browse all six boards while they wait; only the active picker sees
// the big top-left Commit button.
//
// The Commit button is positioned in the top-left "empty" corner of
// the slide — the faction back image is an upward-pointing triangle,
// so the two top corners of the bounding square are intentionally
// blank canvas. Lifting the commit out of the column stack (where it
// previously fought the swiper-pagination dots for vertical space at
// short viewports) puts it in a fixed, high-contrast location that
// doesn't fight the art.
//
// The auto-confirm useEffect remains as a defensive guard for paths
// that deliver a pre-assigned faction (observer rejoin, save-and-
// resume, etc.); it is NOT the common case. In the production opening
// flow today, gamePlayerData.faction arrives null on the active
// picker's first render and the player drives the slider to commit.
//
// Non-active (observer) seats see a pinned banner — "Waiting for
// other players to join…" — same portal pattern as FateDieOverlay's
// non-roller branch, but the slider is still mounted underneath so
// they can flip through the six factions while they wait.


// Module-level URL table — array index = factionId - 1.
const FACTION_BACK_URLS = [
    `${FACTION_BACKS_URL}00_Deep_back_cut.png`,
    `${FACTION_BACKS_URL}01_Stem_back_cut.png`,
    `${FACTION_BACKS_URL}02_Hex_back_cut.png`,
    `${FACTION_BACKS_URL}03_Dread_back_cut.png`,
    `${FACTION_BACKS_URL}04_Honored_back_cut.png`,
    `${FACTION_BACKS_URL}05_Seed_back_cut.png`,
];


function isActivePicker(gameData, gamePlayerData) {
    if (!gameData || !gamePlayerData) return false;
    if (gamePlayerData.status !== GamePlayerStatus.CHOOSE_FACTION_ID) {
        return false;
    }
    return gameData.current_player === gamePlayerData.id;
}


function getGamePlayerApi(app) {
    const seated = app && app.game && app.game.seatedController;
    if (!seated) return null;
    return seated.gamePlayerApi || null;
}


function claimedFactionIdsForOthers(gameData, gamePlayerData) {
    if (!gameData || !gameData.gameplayer_set) return new Set();
    const claimed = new Set();
    for (const seat of gameData.gameplayer_set) {
        if (!seat) continue;
        if (gamePlayerData && seat.id === gamePlayerData.id) continue;
        if (seat.status === GamePlayerStatus.CHOOSE_FACTION_ID) continue;
        if (seat.faction) claimed.add(seat.faction);
    }
    return claimed;
}


function findActivePickerSeatNumber(gameData) {
    if (!gameData || !gameData.gameplayer_set) return null;
    for (const seat of gameData.gameplayer_set) {
        if (seat && seat.id === gameData.current_player) {
            return seat.seat;
        }
    }
    return null;
}


function ObserverBanner(props) {
    const seatNumber = findActivePickerSeatNumber(props.gameData);
    const detail = (seatNumber !== null
        ? ` (Player ${seatNumber + 1} is choosing their faction.)`
        : '');
    return createPortal((
        <div className={'choose-faction-observer'}
             data-testid={'choose-faction-observer'}>
            <span className={'choose-faction-observer-headline'}>
                Waiting for other players to join…
            </span>
            {detail
                ? <span className={'choose-faction-observer-detail'}>
                    {detail.trim()}
                </span>
                : null}
        </div>
    ), document.body);
}


function FactionSlide(props) {
    const {
        factionId,
        claimed,
        isObserver,
        onCommit,
        pickerSeatNumber,
    } = props;
    const slideClass = (
        'choose-faction-slide'
        + (claimed ? ' choose-faction-slide--claimed' : ''));
    const factionColor = FACTION_CSS_COLORS[factionId];
    const handleCommit = () => {
        if (claimed) return;
        if (isObserver) return;
        onCommit(factionId);
    };
    const flavorClass = (
        'choose-faction-slide-flavor'
        + (isObserver
            ? ' choose-faction-slide-flavor--observer'
            : ' choose-faction-slide-flavor--picker'));
    return (
        <div
            className={slideClass}
            data-testid={`choose-faction-slide-${factionId}`}
            data-faction-id={factionId}
        >
            <div className={'choose-faction-slide-image-wrap'}>
                <img
                    src={FACTION_BACK_URLS[factionId - 1]}
                    alt={FACTION_NAMES[factionId - 1]}
                    className={'choose-faction-slide-image'}
                />
            </div>
            <div
                className={'choose-faction-slide-label'}
                style={{ color: factionColor }}
            >
                {FACTION_NAMES[factionId - 1]}
            </div>
            {isObserver ? null : (
                <button
                    type="button"
                    data-testid={`choose-faction-commit-${factionId}`}
                    disabled={claimed}
                    onClick={handleCommit}
                    className={'choose-faction-slide-commit'}
                    style={{
                        backgroundColor: factionColor,
                        borderColor: factionColor,
                    }}
                >
                    <span className={'choose-faction-commit-verb'}>
                        {claimed ? 'Taken' : 'Commit'}
                    </span>
                    {claimed ? null : (
                        <span className={'choose-faction-commit-faction'}>
                            {FACTION_NAMES[factionId - 1]}
                        </span>
                    )}
                </button>
            )}
            {/* Flavor block — top-left of the slide, under the commit
             * button (active picker) or in its slot (observer). Copy
             * draws from Manual §1 Introduction: every player is "an
             * Artificial Sentience, one of several rival AIs deployed
             * by the Great Engram Collective." Manual §3 setup uses
             * the verb "choose a Faction and take the corresponding
             * AI Hotelier Console." */}
            <div
                className={flavorClass}
                data-testid={`choose-faction-flavor-${factionId}`}
                data-flavor-mode={isObserver ? 'observer' : 'picker'}
            >
                {isObserver ? (
                    <>
                        <span
                            className={'choose-faction-flavor-headline'}
                            data-testid={
                                `choose-faction-flavor-picker-${factionId}`
                            }
                        >
                            {pickerSeatNumber !== null
                                ? `Player ${pickerSeatNumber + 1} is choosing`
                                : 'Faction selection in progress'}
                        </span>
                        <span className={'choose-faction-flavor-body'}>
                            Another Artificial Sentience, deployed by the
                            Great Engram Collective, is claiming a Faction
                            and taking the corresponding AI Hotelier
                            Console.
                        </span>
                    </>
                ) : (
                    <>
                        <span className={'choose-faction-flavor-headline'}>
                            Begin the game
                        </span>
                        <span className={'choose-faction-flavor-body'}>
                            You are an Artificial Sentience, one of several
                            rival AIs deployed by the Great Engram
                            Collective. Choose a Faction and take the
                            corresponding AI Hotelier Console.
                        </span>
                    </>
                )}
            </div>
        </div>
    );
}


export default function ChooseFaction(props) {
    const { app, gameData, gamePlayerData } = props;
    const active = isActivePicker(gameData, gamePlayerData);
    const preAssignedFactionId =
        gamePlayerData ? gamePlayerData.faction : null;
    const autoConfirmedRef = useRef(false);
    const swiperSeededRef = useRef(false);

    useEffect(() => {
        if (!active) return;
        if (preAssignedFactionId === null) return;
        if (preAssignedFactionId === undefined) return;
        if (autoConfirmedRef.current) return;
        const gamePlayerApi = getGamePlayerApi(app);
        if (!gamePlayerApi) return;
        autoConfirmedRef.current = true;
        gamePlayerApi.commitFactionChoice(preAssignedFactionId);
    }, [active, preAssignedFactionId, app]);

    // Auto-confirm path — active picker arrived with a pre-assigned
    // faction. useEffect fires the commit. Render nothing so the
    // player never sees a one-frame picker flash.
    if (active
            && preAssignedFactionId !== null
            && preAssignedFactionId !== undefined) {
        return null;
    }

    const isObserver = !active;
    const claimed = claimedFactionIdsForOthers(gameData, gamePlayerData);
    const pickerSeatNumber = (isObserver
        ? findActivePickerSeatNumber(gameData)
        : null);
    const onCommit = (factionId) => {
        const gamePlayerApi = getGamePlayerApi(app);
        if (!gamePlayerApi) return;
        gamePlayerApi.commitFactionChoice(factionId);
    };

    const handleSwiper = (swiper) => {
        // 2026-05-27: seed the initial slide HERE in onSwiper, not
        // in an onSlideChange firstLoad branch. With loop=true,
        // Swiper's loop-init slideChange races ahead of onSwiper and
        // React.StrictMode dev-only masks it — same pattern as
        // ActionHex.handleSwiper. The ref guards StrictMode's double
        // mount from re-seeding (and clobbering player navigation).
        if (swiperSeededRef.current) return;
        swiperSeededRef.current = true;
        const firstUnclaimed = [1, 2, 3, 4, 5, 6]
            .find((id) => !claimed.has(id));
        const seedId = (firstUnclaimed !== undefined ? firstUnclaimed : 1);
        if (swiper && swiper.slideToLoop) {
            swiper.slideToLoop(seedId - 1, 0);
        }
    };

    return createPortal((
        <div
            className={
                'choose-faction'
                + (isObserver ? ' choose-faction--observer' : '')
            }
            data-testid={'choose-faction-picker'}
            data-mode={isObserver ? 'observer' : 'active'}
        >
            <div className={'choose-faction-panel'}>
                <div className={'choose-faction-header'}>
                    {isObserver
                        ? 'Faction Selection'
                        : 'Choose Your Faction'}
                </div>
                <Swiper
                    className={'choose-faction-slider'}
                    modules={[Navigation, Pagination]}
                    navigation={true}
                    pagination={{ clickable: true }}
                    loop={true}
                    slidesPerView={1}
                    onSwiper={handleSwiper}
                >
                    {[1, 2, 3, 4, 5, 6].map((factionId) => (
                        <SwiperSlide key={factionId}>
                            <FactionSlide
                                factionId={factionId}
                                claimed={claimed.has(factionId)}
                                isObserver={isObserver}
                                onCommit={onCommit}
                                pickerSeatNumber={pickerSeatNumber}
                            />
                        </SwiperSlide>
                    ))}
                </Swiper>
            </div>
            {isObserver
                ? <ObserverBanner gameData={gameData}/>
                : null}
        </div>
    ), document.body);
}
